import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { ImageScaleView, MovieClip, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import { getScreenDimensions } from 'src/lib/utils';
import { CardsRibbon } from '../components/CardsRibbon';
import { CardsShine } from '../components/CardsShine';
import gsap from 'gsap';
import { CardsCongratulations } from '../components/CardsCongratulations';
import {
  CardsRewardId,
  CollectionId,
  collectionColors,
} from 'src/replicant/cards/cards.ruleset';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { Match2RewardsPanel } from 'src/game/minigame/match2-timestep/components/Match2RewardsPanel';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import sounds from 'src/lib/sounds';
import { CardCollectionIcon } from '../components/CardCollectionIcon';

type CloseHandler = () => void;

export default class PopupCardsAlbumReward extends PopupBasicLazy {
  private congratulations: CardsCongratulations;
  private shine: CardsShine;
  private ribbon: CardsRibbon;
  private confetti: MovieClip;
  private close: CloseHandler;
  private iconContainer: View;
  private panel: ImageScaleView;
  private rewards: Match2RewardsPanel;
  private claimButton: ButtonScaleViewWithText;
  private text: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = opts.superview.style.width;
    const height = getScreenDimensions().height;

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.POPUP_ALBUM_REWARD,
      },
      zIndex: 10000,
    });

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    this.close = opts.close;

    this.congratulations = new CardsCongratulations({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 200,
      centerOnOrigin: true,
    });

    this.ribbon = new CardsRibbon({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 290,
      centerOnOrigin: true,
      scale: 0,
    });

    this.iconContainer = new View({
      x: this.box.style.width / 2,
      y: this.box.style.height * 0.4,
      centerOnOrigin: true,
      superview: this.box,
      scale: 0,
    });

    this.buttonClose.updateOpts({
      visible: false,
    });

    this.shine = new CardsShine({
      superview: this.iconContainer,
      radius: 250,
      opacity: 0.7,
      scale: 0,
    });

    this.panel = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/match2/common/panel_match2_popup.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 50, right: 50 },
        vertical: { top: 50, bottom: 50 },
      },
      width: 550,
      height: 450,
      x: this.box.style.width * 0.5,
      y: this.box.style.height * 0.75,
      centerOnOrigin: true,
      scale: 0,
    });

    this.rewards = new Match2RewardsPanel({
      superview: this.panel,
      x: this.panel.style.width / 2,
      y: this.panel.style.height / 2 - 20,
      width: this.panel.style.width - 30,
      height: this.panel.style.height - 100,
      itemSize: 100,
      itemPadding: 10,
      itemOffsetX: 10,
      itemOffsetY: 10,
      itemLabelOffsetX: -10,
      itemLabelOffsetY: -10,
      noBg: true,
    });

    this.text = new LangBitmapFontTextView({
      superview: this.panel,
      align: 'center',
      verticalAlign: 'center',
      size: 40,
      font: 'Title',
      color: '#FFFFFF',
      width: this.panel.style.width - 30,
      height: 80,
      centerOnOrigin: true,
      x: this.panel.style.width / 2,
      y: 80,
    });

    this.claimButton = new ButtonScaleViewWithText({
      superview: this.panel,
      ...buttons.secondary,
      width: 317,
      height: 95,
      fontSize: 40,
      labelOffsetY: -2,
      centerOnOrigin: true,
      x: this.panel.style.width / 2,
      y: this.panel.style.height - 100,
      font: 'Body',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.CLAIM_ALBUM_REWARDS_BUTTON,
      },
      localeText: () => i18n('cards.packRewardPopup.claim'),
      onClick: async () => this.claim(),
    });

    this.confetti = new MovieClip({
      superview: this.box,
      url: 'assets/ui/match2/movieclips/level_complete_stars',
      x: width / 2,
      y: this.iconContainer.style.y + 300,
    });
  }

  public getAssetsGroup() {
    return new AssetGroup(AssetGroup.constructURLs(['assets/ui/cards/common']));
  }

  public async init(args: {
    rewardId: CardsRewardId;
    rewards: Match2Reward[];
  }) {
    super.init({});

    if (args.rewardId === 'album') {
      this.setupAsAlbumRewards();
    } else {
      this.setupAsCollectionRewards(args.rewardId);
    }
    this.text.localeText = () => i18n('cards.albumRewardPopup.yourRewards');
    this.rewards.setRewards(args.rewards);
  }

  public async fadeIn() {
    this.overlay.show();
    this.attachRoot();
    this.box.hide();
    this.bg.updateOpts({ opacity: 0 });
    await this.loadAssets();
    this.overlay.hide();
    this.box.show();
    await gsap.to(this.bg.style, {
      opacity: 1,
      duration: 0.2,
      ease: 'linear',
    });
    this.showAnimated();
  }

  public async fadeOut() {
    this.overlay.show();
    this.shine.stopAutoUpdate();
    animate(this.box)
      .clear()
      .wait(0)
      .then({ opacity: 0 }, 200, animate.easeOut)
      .toPromise()
      .then(() => {
        this.detachRoot();
        this.overlay.hide();
      });
  }

  private setupAsAlbumRewards() {
    new ImageScaleView({
      superview: this.iconContainer,
      image: 'assets/ui/cards/common/icon_album.png',
      width: 296,
      height: 300,
      scaleMethod: 'contain',
      centerOnOrigin: true,
    });
    this.ribbon.setText(i18n('cards.albumRewardPopup.albumComplete'));
    this.claimButton.tracking.$subFeature =
      Feature.CARDS.CLAIM_ALBUM_REWARDS_BUTTON;
  }

  private setupAsCollectionRewards(collectionId: CollectionId) {
    const icon = new CardCollectionIcon({
      superview: this.iconContainer,
      centerOnOrigin: true,
    });

    icon.setState({
      id: collectionId,
      totalCards: 9,
      collected: 9,
      color: collectionColors[collectionId] as string,
    });

    this.ribbon.setText(i18n('cards.albumRewardPopup.setComplete'));
    this.claimButton.tracking.$subFeature =
      Feature.CARDS.CLAIM_COLLECTION_REWARDS_BUTTON;
  }

  private async showAnimated() {
    sounds.sfx.play('gachaItemDrop');

    await this.congratulations.showAnimated();

    await gsap.to(this.ribbon.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    await gsap.to(this.iconContainer.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    this.shine.autoUpdate();
    await gsap.to(this.shine.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });

    this.confetti.play(`staranim_confetti`);
    sounds.sfx.play('applause');

    await gsap.to(this.panel.style, {
      scale: 1,
      duration: 0.3,
      ease: 'back.out',
    });
  }

  private async claim() {
    sounds.sfx.play('win');
    this.claimButton.hide();
    this.close();
  }
}
